import { useEffect, useState, useCallback, useRef } from 'react';
import './PlatformCharges.css';
import Layout from '../../../../components/Layout';
import { Box, Typography, Tabs, Tab } from '@mui/material';
import AdminNavbar from '../../../../components/Admin/AdminNavbar/AdminNavbar';
import { axiosInstance } from '../../../../utils/tokenUtils';
import { toast } from 'react-hot-toast';
import PlatformChargesEdit from '../../../../components/Admin/FeeSettings/PlatformCharges/PlatfromChargesEdit';
import PlatformCreativeArts from '../../../../components/Admin/FeeSettings/PlatformCharges/PlatformCreativeArts';
import {
  TableSkeleton,
  CardSkeleton,
} from '../../../../components/Admin/SkeletonLoader/SkeletonLoader';
import Academic from './Academic/Academic';
import Creative from './Creative/Creative';

const PlatformCharges = () => {
  const [expanded, setExpanded] = useState(null);
  const [creativeArtsExpanded, setCreativeArtsExpanded] = useState(null);
  const [data, setData] = useState([]);
  const [creativeArtsAccordionData, setCreativeArtsAccordionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCreativeArtsAccordion, setLoadingCreativeArtsAccordion] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [creativeArtsData, setCreativeArtsData] = useState({
    min: '0',
    max: '0',
  });
  const [isLoadingCreativeArts, setIsLoadingCreativeArts] = useState(false);
  const [conversionRate, setConversionRate] = useState(7.5);
  const [rbacMe, setRbacMe] = useState(null);
  const [hasLoadedCreativeData, setHasLoadedCreativeData] = useState(false);
  const [tabSkeletonLoading, setTabSkeletonLoading] = useState(false);
  const tabSkeletonTimeoutRef = useRef(null);

  // Modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedSessionData, setSelectedSessionData] = useState(null);

  // Creative Arts Modal state
  const [creativeArtsModalOpen, setCreativeArtsModalOpen] = useState(false);
  const [selectedCreativeArtsData, setSelectedCreativeArtsData] = useState(null);

  // Helper function to check if user has a specific permission
  const hasPermission = permission => {
    if (!rbacMe || !Array.isArray(rbacMe.permissions)) return false;
    return rbacMe.permissions.includes(permission);
  };

  // Check if current user can manage fee settings
  const canManageFeeSettings = () => {
    return hasPermission('manage_fee_settings') || (rbacMe?.roles || []).includes('super_admin');
  };

  // Load current user permissions
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        let cachedData = null;
        try {
          const cached = sessionStorage.getItem('rbac_admin_me');
          if (cached) {
            cachedData = JSON.parse(cached);
            setRbacMe(cachedData);
          }
        } catch (_) {}

        const { data } = await axiosInstance.get('/admin/rbac/admin/me');
        setRbacMe(data);

        try {
          sessionStorage.setItem('rbac_admin_me', JSON.stringify(data));
        } catch (_) {}
      } catch (_) {
        if (!rbacMe) setRbacMe({ roles: [], permissions: [] });
      }
    };

    fetchCurrentUser();
  }, []);

  // Tooltip content for each field
  const tooltipContent = {
    typeOfSession: 'The category of session being conducted (One-on-One, Group, or Prime)',
    total: 'The total fee amount charged to learners for this session type',
    platformFees:
      'The portion of the total fee that goes to various services like payment processing, Cal.com',
    klassCommission: 'The commission earned by Klass from each session',
    teacher: 'The amount earned by the instructor for conducting this session',
    feeRange: 'The range of fees applicable for Creative Arts sessions',
    minimumFee: 'The minimum fee that can be charged for Creative Arts sessions',
    maximumFee: 'The maximum fee that can be charged for Creative Arts sessions',
    amount:
      'The maximum and minimum amount that can be charged from learner for creative arts sessions',
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (tabSkeletonTimeoutRef.current) {
      clearTimeout(tabSkeletonTimeoutRef.current);
    }
    setTabSkeletonLoading(true);
    tabSkeletonTimeoutRef.current = setTimeout(() => {
      setTabSkeletonLoading(false);
    }, 300);
  };

  // Get conversion rate from API
  const getConversionRate = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/auth/koins-to-riyal');
      if (response.data && response.data.conversion_rate) {
        setConversionRate(response.data.conversion_rate);
      }
    } catch (error) {
      console.error('Error fetching conversion rate:', error);
      // Keep default value of 7.5 if API fails
    }
  }, []);

  // Transform the API data to match the UI structure
  const transformApiData = apiData => {
    // Define the mapping between API values and UI display values
    const sessionTypeMap = {
      one_on_one: 'One-One-Session',
      group: 'Group Session',
      prime: 'Prime Session',
    };

    const schoolLevelMap = {
      elementary: 'Elementary',
      high_school: 'High School',
      middle_school: 'Middle School',
      university: 'University',
    };

    // Define the desired order of session types
    const sessionTypeOrder = ['One-One-Session', 'Group Session', 'Prime Session'];

    // Create the structure needed for the UI
    const categories = [
      { name: 'Elementary', sessions: [] },
      { name: 'High School', sessions: [] },
      { name: 'Middle School', sessions: [] },
      { name: 'University', sessions: [] },
    ];

    // Fill in the sessions for each category
    apiData.forEach(item => {
      const categoryName = schoolLevelMap[item.school_level];
      const sessionType = sessionTypeMap[item.session_type];

      const categoryIndex = categories.findIndex(cat => cat.name === categoryName);
      if (categoryIndex !== -1) {
        categories[categoryIndex].sessions.push({
          type: sessionType,
          // Store all three variants of each value
          total: {
            koins: item.total_fee_koins,
            percentage: 100, // Total is always 100%
            riyals: item.total_fee_riyals,
          },
          cutout: {
            koins: item.service_fee_koins,
            percentage: item.service_fee_percentage,
            riyals: item.service_fee_riyals,
          },
          klassCom: {
            koins: item.klass_commission_koins,
            percentage: item.klass_commission_percentage,
            riyals: item.klass_commission_riyals,
          },
          teacher: {
            koins: item.instructor_commission_koins,
            percentage: item.instructor_commission_percentage,
            riyals: item.instructor_commission_riyals,
          },
          duration: 60, // Default duration
          id: item.id,
          apiSessionType: item.session_type,
          apiSchoolLevel: item.school_level,
        });
      }
    });

    // Sort the sessions in each category according to the desired order
    categories.forEach(category => {
      category.sessions.sort((a, b) => {
        return sessionTypeOrder.indexOf(a.type) - sessionTypeOrder.indexOf(b.type);
      });
    });

    return categories;
  };

  const getPlatformCharges = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(`/admin/academic-fee-settings?page=1&page_size=12`);
      // Handle the new paginated response structure
      const apiData = data.results || data;
      setData(transformApiData(apiData));
    } catch (error) {
      console.log(error);
      // toast.error('Failed to load platform charges. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch Creative Arts fee range data
  const getCreativeArtsFeeRange = useCallback(async () => {
    try {
      setIsLoadingCreativeArts(true);
      const response = await axiosInstance.get('/admin/creative-art-fee-range');

      // The response contains min_fee_koins and max_fee_koins
      if (response.data) {
        const min = response.data.min_fee_koins || 0;
        const max = response.data.max_fee_koins || 0;

        setCreativeArtsData({
          min: min.toString(),
          max: max.toString(),
        });
      }
    } catch (error) {
      console.log(error);
      // toast.error('Failed to load creative arts fee range. Please try again.');
    } finally {
      setIsLoadingCreativeArts(false);
    }
  }, []);

  // Fetch Creative Arts accordion data
  const getCreativeArtsAccordionData = useCallback(async () => {
    try {
      setLoadingCreativeArtsAccordion(true);
      // Use the correct API endpoint for creative arts fee settings
      const { data } = await axiosInstance.get(`/admin/creative-fee-settings`);

      // Transform the creative arts API data for display
      // Handle the new paginated response structure
      const apiData = data.results || data;
      const transformedData = transformCreativeArtsApiData(apiData);
      setCreativeArtsAccordionData(transformedData);
    } catch (error) {
      console.log(error);
      // toast.error('Failed to load creative arts fee structure. Please try again.');
    } finally {
      setLoadingCreativeArtsAccordion(false);
    }
  }, []);

  // Transform creative arts API data for display
  const transformCreativeArtsApiData = apiData => {
    // Define the mapping between API values and UI display values
    const sessionTypeMap = {
      one_on_one: 'One-One-Session',
      group: 'Group Session',
      prime: 'Prime Session',
    };

    const levelMap = {
      beginner: 'Beginner',
      intermediate: 'Intermediate',
      professional: 'Professional',
    };

    // Define the desired order of session types
    const sessionTypeOrder = ['One-One-Session', 'Group Session', 'Prime Session'];

    // Create the structure for Creative Arts levels
    const categories = [
      { name: 'Beginner', sessions: [] },
      { name: 'Intermediate', sessions: [] },
      { name: 'Professional', sessions: [] },
    ];

    // Check if we have valid data
    if (!apiData || !Array.isArray(apiData) || apiData.length === 0) {
      // Return empty categories with placeholder data for display
      categories.forEach(category => {
        sessionTypeOrder.forEach(sessionType => {
          category.sessions.push({
            type: sessionType,
            cutout: { koins: 0, percentage: 0, riyals: 0 },
            klassCom: { koins: 0, percentage: 0, riyals: 0 },
            teacher: { koins: 0, percentage: 0, riyals: 0 },
            duration: 60,
            id: null,
            apiSessionType: sessionType
              .toLowerCase()
              .replace('-', '_')
              .replace('_one_session', '_on_one'),
            apiSchoolLevel: category.name.toLowerCase(),
          });
        });
      });
      return categories;
    }

    // Group data by level and session type
    const levelGroups = {};
    apiData.forEach(item => {
      const level = levelMap[item.level];
      const sessionType = sessionTypeMap[item.session_type];

      if (!levelGroups[level]) {
        levelGroups[level] = {};
      }

      if (sessionType) {
        levelGroups[level][sessionType] = item;
      }
    });

    // For each creative arts level, populate the session data
    categories.forEach(category => {
      const levelData = levelGroups[category.name];

      sessionTypeOrder.forEach(sessionType => {
        const sessionData = levelData?.[sessionType];

        if (sessionData) {
          category.sessions.push({
            type: sessionType,
            // Use the updated field names from the new API structure
            cutout: {
              koins: sessionData.service_fee_min_koins || 0,
              maxKoins: sessionData.service_fee_max_koins || 0,
              percentage: sessionData.service_fee_percentage || 0,
              riyals: sessionData.service_fee_min_riyals || 0,
            },
            klassCom: {
              koins: sessionData.klass_commission_min_koins || 0,
              maxKoins: sessionData.klass_commission_max_koins || 0,
              percentage: sessionData.klass_commission_percentage || 0,
              riyals: sessionData.klass_commission_min_riyals || 0,
            },
            teacher: {
              koins: sessionData.instructor_commission_min_koins || 0,
              maxKoins: sessionData.instructor_commission_max_koins || 0,
              percentage: sessionData.instructor_commission_percentage || 0,
              riyals: sessionData.instructor_commission_min_riyals || 0,
            },
            duration: 60,
            id: sessionData.id,
            apiSessionType: sessionData.session_type,
            apiSchoolLevel: sessionData.level,
          });
        } else {
          // Fallback if no data for this session type
          category.sessions.push({
            type: sessionType,
            cutout: { koins: 0, percentage: 0, riyals: 0 },
            klassCom: { koins: 0, percentage: 0, riyals: 0 },
            teacher: { koins: 0, percentage: 0, riyals: 0 },
            duration: 60,
            id: null,
            apiSessionType: sessionType
              .toLowerCase()
              .replace('-', '_')
              .replace('_one_session', '_on_one'),
            apiSchoolLevel: category.name.toLowerCase(),
          });
        }
      });
    });

    return categories;
  };

  useEffect(() => {
    getConversionRate();
    getPlatformCharges();
  }, [getPlatformCharges, getConversionRate]);

  useEffect(() => {
    if (activeTab === 1 && !hasLoadedCreativeData) {
      getCreativeArtsFeeRange();
      getCreativeArtsAccordionData();
      setHasLoadedCreativeData(true);
    }
  }, [activeTab, hasLoadedCreativeData, getCreativeArtsFeeRange, getCreativeArtsAccordionData]);

  useEffect(() => {
    return () => {
      if (tabSkeletonTimeoutRef.current) {
        clearTimeout(tabSkeletonTimeoutRef.current);
      }
    };
  }, []);

  // Handle saving the Creative Arts fee range
  const handleSaveCreativeArts = async () => {
    if (!canManageFeeSettings()) {
      toast.dismiss();
      toast.error("You don't have permission to manage fee settings");
      return;
    }

    try {
      // Validate inputs
      if (
        !/^\d+(\.\d+)?$/.test(creativeArtsData.min) ||
        !/^\d+(\.\d+)?$/.test(creativeArtsData.max)
      ) {
        toast.error('Please enter valid positive numbers for minimum and maximum fee');
        return;
      }

      if (parseFloat(creativeArtsData.min) > parseFloat(creativeArtsData.max)) {
        toast.error('Minimum fee cannot be greater than maximum fee');
        return;
      }

      setIsLoadingCreativeArts(true);

      // Use PATCH method to update creative arts fee range
      const response = await axiosInstance.patch('/admin/creative-art-fee-range', {
        min_fee_koins: parseFloat(creativeArtsData.min),
        max_fee_koins: parseFloat(creativeArtsData.max),
      });

      if (response.status === 200) {
        toast.success('Creative Arts fee range updated successfully');
        // Refresh the data
        getCreativeArtsFeeRange();
        getCreativeArtsAccordionData();
      }
    } catch (error) {
      console.error('Error updating creative arts fee range:', error);

      // Handle API error responses
      if (error.response && error.response.data) {
        const errorData = error.response.data;

        // Show the detail message from the server if available
        if (errorData.detail) {
          toast.error(errorData.detail);
        } else if (errorData.message) {
          toast.error(errorData.message);
        } else if (typeof errorData === 'string') {
          toast.error(errorData);
        } else {
          toast.error('Failed to update fee range. Please try again.');
        }
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('Failed to update fee range. Please try again.');
      }
    } finally {
      setIsLoadingCreativeArts(false);
    }
  };

  const handleAccordionClick = categoryName => {
    setExpanded(expanded === categoryName ? null : categoryName);
  };

  const handleCreativeArtsAccordionClick = categoryName => {
    setCreativeArtsExpanded(creativeArtsExpanded === categoryName ? null : categoryName);
  };

  const handleEdit = (categoryIndex, sessionIndex) => {
    if (!canManageFeeSettings()) {
      toast.dismiss();
      toast.error("You don't have permission to manage fee settings");
      return;
    }

    const category = data[categoryIndex];
    const session = category.sessions[sessionIndex];

    setSelectedSessionData(session);
    setEditModalOpen(true);
  };

  const handleCreativeArtsEdit = (categoryIndex, sessionIndex) => {
    if (!canManageFeeSettings()) {
      toast.dismiss();
      toast.error("You don't have permission to manage fee settings");
      return;
    }

    const category = creativeArtsAccordionData[categoryIndex];
    const session = category.sessions[sessionIndex];

    // Calculate total min and max fees from the commission data
    const totalMinFee =
      (session.klassCom?.koins || 0) + (session.cutout?.koins || 0) + (session.teacher?.koins || 0);

    const totalMaxFee =
      (session.klassCom?.maxKoins || 0) +
      (session.cutout?.maxKoins || 0) +
      (session.teacher?.maxKoins || 0);

    setSelectedCreativeArtsData({
      ...session,
      categoryName: category.name,
      minFee: totalMinFee,
      maxFee: totalMaxFee,
    });
    setCreativeArtsModalOpen(true);
  };

  const handleModalClose = () => {
    setEditModalOpen(false);
    setSelectedSessionData(null);
  };

  const handleCreativeArtsModalClose = () => {
    setCreativeArtsModalOpen(false);
    setSelectedCreativeArtsData(null);
  };

  const handleModalSaveSuccess = () => {
    // Refresh the data after successful save
    getPlatformCharges();
  };

  const handleCreativeArtsModalSaveSuccess = () => {
    // Refresh the Creative Arts data after successful save
    getCreativeArtsFeeRange();
    getCreativeArtsAccordionData();
  };

  if (activeTab === 0 && (loading || tabSkeletonLoading)) {
    return (
      <Layout title="Klass Online | Platform Charges">
        <Box className="relative h-screen w-full overflow-hidden platform-charges-page">
          <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
            <AdminNavbar />
          </Box>
          <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
            <Box className="flex flex-col p-[1rem] gap-3">
              <Box className="flex justify-between items-center">
                <Typography className="platform-charges-title">Platform Charges</Typography>
              </Box>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={0} aria-label="platform charges tabs">
                  <Tab label="Academic" sx={{ fontWeight: 600 }} />
                  <Tab label="Creative Arts" sx={{ fontWeight: 400 }} />
                </Tabs>
              </Box>
              <Box sx={{ mt: 2 }}>
                <TableSkeleton />
              </Box>
            </Box>
          </Box>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout title="Klass Online | Platform Charges">
      <Box className="relative h-screen w-full overflow-hidden platform-charges-page">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>
        <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
          <Box className="flex flex-col p-[1rem] gap-3">
            <Box className="flex justify-between items-center">
              <Typography className="platform-charges-title">Platform Charges</Typography>
            </Box>

            {/* Tabs Section */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={activeTab} onChange={handleTabChange} aria-label="platform charges tabs">
                <Tab label="Academic" sx={{ fontWeight: activeTab === 0 ? 600 : 400 }} />
                <Tab label="Creative Arts" sx={{ fontWeight: activeTab === 1 ? 600 : 400 }} />
              </Tabs>
            </Box>

            {/* Academic Tab Content */}
            {activeTab === 0 && (
              <Academic
                data={data}
                expanded={expanded}
                handleAccordionClick={handleAccordionClick}
                handleEdit={handleEdit}
                tooltipContent={tooltipContent}
                conversionRate={conversionRate}
                canManageFeeSettings={canManageFeeSettings}
              />
            )}

            {/* Creative Arts Tab Content */}
            {activeTab === 1 &&
              (tabSkeletonLoading ||
              isLoadingCreativeArts ||
              loadingCreativeArtsAccordion ||
              !hasLoadedCreativeData ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <CardSkeleton />
                  <TableSkeleton />
                </Box>
              ) : (
                <Creative
                  creativeArtsData={creativeArtsData}
                  setCreativeArtsData={setCreativeArtsData}
                  isLoadingCreativeArts={isLoadingCreativeArts}
                  handleSaveCreativeArts={handleSaveCreativeArts}
                  creativeArtsAccordionData={creativeArtsAccordionData}
                  loadingCreativeArtsAccordion={loadingCreativeArtsAccordion}
                  creativeArtsExpanded={creativeArtsExpanded}
                  handleCreativeArtsAccordionClick={handleCreativeArtsAccordionClick}
                  handleCreativeArtsEdit={handleCreativeArtsEdit}
                  tooltipContent={tooltipContent}
                  conversionRate={conversionRate}
                  canManageFeeSettings={canManageFeeSettings}
                />
              ))}
          </Box>
        </Box>

        {/* Platform Charges Edit Modal */}
        <PlatformChargesEdit
          open={editModalOpen}
          onClose={handleModalClose}
          sessionData={selectedSessionData}
          onSaveSuccess={handleModalSaveSuccess}
          conversionRate={conversionRate}
        />

        {/* Creative Arts Edit Modal */}
        <PlatformCreativeArts
          open={creativeArtsModalOpen}
          onClose={handleCreativeArtsModalClose}
          sessionData={selectedCreativeArtsData}
          onSaveSuccess={handleCreativeArtsModalSaveSuccess}
          conversionRate={conversionRate}
        />
      </Box>
    </Layout>
  );
};

export default PlatformCharges;
